import React, { useState, useEffect, useRef, useCallback } from 'react';
import { messageAPI, patientAPI, doctorAPI } from '../../lib/api';
import { formatDateTime, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MessageSquare, Send, User, FlaskConical, Pill, Stethoscope, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const DEPARTMENTS = [
  { value: 'doctor', label: 'Doctors', icon: Stethoscope },
  { value: 'lab', label: 'Laboratory', icon: FlaskConical },
  { value: 'pharmacy', label: 'Pharmacy', icon: Pill },
  { value: 'nurse', label: 'Nursing', icon: User },
];

const MESSAGE_TYPES = [
  { value: 'general', label: 'General Message' },
  { value: 'lab_request', label: 'Lab Test Request' },
  { value: 'pharmacy_request', label: 'Pharmacy Request' },
  { value: 'consultation_request', label: 'Consultation Request' },
];

const PRIORITIES = [
  { value: 'normal', label: 'Normal', color: 'bg-slate-100 text-slate-800' },
  { value: 'urgent', label: 'Urgent', color: 'bg-amber-100 text-amber-800' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
];

const CaseMessages = () => {
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [caseMessages, setCaseMessages] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    message: '',
    recipient_department: '',
    recipient_user_id: '',
    priority: 'normal',
    message_type: 'general',
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [caseMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchInitialData = async () => {
    try {
      const [patRes, docRes, msgRes] = await Promise.all([
        patientAPI.getAll({}),
        doctorAPI.getAll(),
        messageAPI.getAll({}),
      ]);
      setPatients(patRes.data.patients || patRes.data || []);
      setDoctors(docRes.data.doctors || docRes.data || []);
      setMessages(msgRes.data.messages || msgRes.data || []);
    } catch (error) {
      logger.error('Error loading case messages:', error);
      toast.error(apiErrorMessage(error, 'Failed to load data. Please try again.'));
      setPatients([]);
      setDoctors([]);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseMessages = useCallback(async () => {
    try {
      const response = await messageAPI.getByPatient(selectedPatient);
      setCaseMessages(response.data.messages || response.data || []);
    } catch (error) {
      logger.error('Failed to load messages:', error);
      toast.error('Failed to load messages. Please try again.');
      setCaseMessages([]);
    }
  }, [selectedPatient]);


  useEffect(() => {
    if (selectedPatient) {
      fetchCaseMessages();
    }
  }, [selectedPatient, fetchCaseMessages]);
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!formData.message.trim() || !selectedPatient) return;

    try {
      await messageAPI.send({
        patient_id: selectedPatient,
        ...formData,
      });
      toast.success('Message sent');
      setFormData({
        message: '',
        recipient_department: formData.recipient_department,
        recipient_user_id: formData.recipient_user_id,
        priority: 'normal',
        message_type: 'general',
      });
      fetchCaseMessages();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(e => e.msg || e).join(', ')
            : error.response.data.detail)
        : 'Failed to send message';
      toast.error(errorMsg);
    }
  };

  const getSelectedPatient = () => patients.find(p => p.id === selectedPatient);

  const filteredPatients = patients.filter(p => {
    if (!searchTerm) return true;
    return p.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           p.patient_id.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority);
    return p ? p.color : 'bg-slate-100 text-slate-800';
  };

  // Group messages by patient for sidebar
  const patientMessageCounts = messages.reduce((acc, msg) => {
    if (!acc[msg.patient_id]) {
      acc[msg.patient_id] = { count: 0, unread: 0, patient: msg.patient };
    }
    acc[msg.patient_id].count++;
    if (!msg.is_read && msg.sender_id !== user.id) {
      acc[msg.patient_id].unread++;
    }
    return acc;
  }, {});

  return (
    <div data-testid="case-messages-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Case Messages</h1>
            <p className="text-slate-500">Communicate with departments about patient cases</p>
          </div>
        </div>
      </header>

      <div className="page-content">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
          {/* Patient List Sidebar */}
          <Card className="lg:col-span-1 flex flex-col">
            <CardHeader className="pb-2 border-b">
              <CardTitle className="text-lg font-semibold font-[Manrope]">Patients</CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search patients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-patients"
                />
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <p className="text-center text-slate-500 py-4">Loading...</p>
              ) : (
                <div className="space-y-1">
                  {filteredPatients.map((patient) => (
                    <button
                      key={patient.id}
                      onClick={() => setSelectedPatient(patient.id)}
                      className={`w-full p-3 rounded-lg text-left transition-colors ${
                        selectedPatient === patient.id
                          ? 'bg-red-50 border border-red-200'
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid={`patient-${patient.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{patient.full_name}</p>
                          <p className="text-xs text-slate-500">{patient.patient_id}</p>
                        </div>
                        {patientMessageCounts[patient.id]?.unread > 0 && (
                          <Badge className="bg-red-500 text-white text-xs">
                            {patientMessageCounts[patient.id].unread}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Message Area */}
          <Card className="lg:col-span-3 flex flex-col">
            {!selectedPatient ? (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Select a patient to view and send messages</p>
                </div>
              </div>
            ) : (
              <>
                <CardHeader className="border-b pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold font-[Manrope]">
                        {getSelectedPatient()?.full_name}
                      </CardTitle>
                      <p className="text-sm text-slate-500">{getSelectedPatient()?.patient_id}</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800">
                      {caseMessages.length} messages
                    </Badge>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '400px' }}>
                  {caseMessages.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>No messages for this case yet</p>
                    </div>
                  ) : (
                    caseMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.sender_id === user.id
                              ? 'bg-red-500 text-white'
                              : 'bg-slate-100 text-slate-900'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-medium ${msg.sender_id === user.id ? 'text-red-100' : 'text-slate-500'}`}>
                              {msg.sender_name} ({msg.sender_role?.replace('_', ' ')})
                            </span>
                            {msg.priority !== 'normal' && (
                              <Badge className={`text-xs ${msg.sender_id === user.id ? 'bg-white/20 text-white' : getPriorityBadge(msg.priority)}`}>
                                {msg.priority}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm">{msg.message}</p>
                          <div className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-red-200' : 'text-slate-400'}`}>
                            {formatDateTime(msg.created_at)}
                            {msg.message_type !== 'general' && (
                              <span className="ml-2">• {msg.message_type.replace('_', ' ')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>

                {/* Send Message Form */}
                <div className="border-t p-4">
                  <form onSubmit={handleSendMessage} className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Send To</Label>
                        <Select value={formData.recipient_department} onValueChange={(v) => setFormData({...formData, recipient_department: v, recipient_user_id: ''})}>
                          <SelectTrigger data-testid="select-department">
                            <SelectValue placeholder="Department" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {DEPARTMENTS.map(dept => (
                              <SelectItem key={dept.value} value={dept.value}>
                                <div className="flex items-center gap-2">
                                  <dept.icon className="w-4 h-4" />
                                  {dept.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Message Type</Label>
                        <Select value={formData.message_type} onValueChange={(v) => setFormData({...formData, message_type: v})}>
                          <SelectTrigger data-testid="select-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {MESSAGE_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Priority</Label>
                        <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                          <SelectTrigger data-testid="select-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {PRIORITIES.map(p => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.recipient_department === 'doctor' && (
                        <div>
                          <Label className="text-xs">Specific Doctor</Label>
                          <Select value={formData.recipient_user_id} onValueChange={(v) => setFormData({...formData, recipient_user_id: v})}>
                            <SelectTrigger data-testid="select-doctor">
                              <SelectValue placeholder="All doctors" />
                            </SelectTrigger>
                            <SelectContent position="popper" sideOffset={5}>
                              {doctors.map(doc => (
                                <SelectItem key={doc.id} value={doc.id}>Dr. {doc.full_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Type your message..."
                        className="flex-1"
                        data-testid="input-message"
                      />
                      <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="send-message">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CaseMessages;
